// Redemption flow: code in, event egg out (EVENT-CODES.md §3).
//
// The server is the authority on single use. Everything this module does
// locally — shape checks, the already-spent ledger — is there to give a kid a
// fast, clear answer without a round trip, not to decide anything.

import { Rng } from '../core/rng';
import type { GameStateStore } from '../core/store';
import { applyGrant, type GrantResult } from '../sim/eventBirds';
import {
  grantFromResponse,
  isRedeemedLocally,
  normalizeCode,
  recordRedemption,
  validateCodeShape,
  type CodeError,
  type GrantPayload,
} from '../sim/eventCodes';
import type { SupabaseClient } from './supabase';

export type RedeemOutcome =
  | { status: 'redeemed'; payload: GrantPayload; granted: GrantResult }
  | { status: 'rejected'; reason: CodeError }
  | { status: 'failed'; reason: 'invalid_or_used' | 'rate_limited' | 'wave_closed' | 'offline' | 'server' }
  | { status: 'needs_account' }
  | { status: 'unavailable' };

/** Loose on purpose: the shape depends on what the code grants. */
type RedeemResponse = Record<string, unknown>;

export async function redeemCode(
  store: GameStateStore,
  client: SupabaseClient,
  rawCode: string,
): Promise<RedeemOutcome> {
  const shapeProblem = validateCodeShape(rawCode);
  if (shapeProblem) return { status: 'rejected', reason: shapeProblem };

  const code = normalizeCode(rawCode);
  if (isRedeemedLocally(store.getState(), code)) {
    return { status: 'rejected', reason: 'already_redeemed' };
  }

  if (!client.configured) return { status: 'unavailable' };
  // Redemption ties the collectible to the family account, so it needs a login.
  if (!client.signedIn) return { status: 'needs_account' };

  const result = await client.callFunction<RedeemResponse>('clayfarm-redeem', { code });

  if (!result.ok) {
    if (result.error === 'offline') return { status: 'failed', reason: 'offline' };
    if (result.error === 'unauthorized') return { status: 'needs_account' };
    // The function encodes its refusals in the HTTP status.
    if (result.detail === '429') return { status: 'failed', reason: 'rate_limited' };
    if (result.detail === '409') return { status: 'failed', reason: 'wave_closed' };
    if (result.detail === '404' || result.detail === '400') {
      return { status: 'failed', reason: 'invalid_or_used' };
    }
    return { status: 'failed', reason: 'server' };
  }

  const payload = grantFromResponse(result.data);

  let granted: GrantResult | null = null;
  store.mutate((state) => {
    recordRedemption(state, code);
    granted = applyGrant(state, payload, new Rng(state.seed + state.simHour));
  });

  return { status: 'redeemed', payload, granted: granted! };
}
